import { useEffect, useState } from 'react';
import { CheckCircle, Clock } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card } from '../../new-components/ui/card';
import { Button } from '../../new-components/ui/button';
import { useUser } from '../../../contexts/UserContext';
import api from '../../../services/api';
import { KycStatusBadge } from '../KycStatusBadge';
import { SectionHeader, InfoList, InfoRow, PRIMARY_BTN } from '../ui';

const EMPTY_FORM = (user: any) => ({
  email: user?.email || '',
  firstName: user?.first_name || '',
  lastName: user?.last_name || '',
  phone: user?.phone || '',
  businessName: user?.business_name || '',
  businessAddress: user?.business_address || '',
  addressLine1: user?.address_line1 || '',
  addressLine2: user?.address_line2 || '',
  city: user?.city || '',
  state: user?.state || '',
  zipCode: user?.zip_code || '',
  country: user?.country || '',
});

/**
 * Profile Information — self-contained Settings section. A single Edit control at the top flips
 * every editable group (Personal, Address, Business) into inputs at once; Save/Cancel replace it
 * while editing. View is a grouped, divided definition list; Account Details stays read-only. The
 * section title lives on the SettingsDetail page (<h1>).
 */
export function ProfileSection() {
  const { user, refreshUser } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM(user));

  // Only resync the form from `user` while not editing, so a background UserContext refresh
  // (KYC/2FA completing elsewhere, another tab) can't overwrite in-progress edits.
  useEffect(() => {
    if (user && !isEditing) setFormData(EMPTY_FORM(user));
  }, [user, isEditing]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData(EMPTY_FORM(user));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.put('/users/profile', {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        businessName: formData.businessName,
        businessAddress: formData.businessAddress,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        country: formData.country,
      });
      toast.success('Profile updated successfully');
      setIsEditing(false);
      await refreshUser();
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const inputClass =
    'w-full h-11 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-foreground';
  const fieldLabel = 'mb-1.5 block text-sm font-medium text-foreground';

  const emailBadge = user?.email_verified ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--success))]/10 px-2 py-0.5 text-xs font-medium text-[hsl(var(--success))]">
      <CheckCircle className="h-3 w-3" aria-hidden="true" /> Verified
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground">
      <Clock className="h-3 w-3" aria-hidden="true" /> Unverified
    </span>
  );

  const readVal = (v: string, empty = 'Not provided') =>
    v ? v : <span className="font-normal text-muted-foreground">{empty}</span>;

  const field = (name: keyof typeof formData, label: string, placeholder: string, colSpanClass = '') => (
    <div className={colSpanClass}>
      <label className={fieldLabel}>{label}</label>
      <input
        type="text"
        name={name}
        value={formData[name]}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={inputClass}
      />
    </div>
  );

  // One edit mode for the whole profile: the single Edit / Save+Cancel control lives at the top
  // (below), not per section.
  // Save only enables once something actually changed (dirty check vs the loaded user values).
  const isDirty = JSON.stringify(formData) !== JSON.stringify(EMPTY_FORM(user));

  const editControls = isEditing ? (
    <div className="flex gap-2">
      <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving}>Cancel</Button>
      <Button size="sm" onClick={handleSave} disabled={isSaving || !isDirty} className={PRIMARY_BTN}>{isSaving ? 'Saving…' : 'Save'}</Button>
    </div>
  ) : (
    <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>Edit</Button>
  );

  // Each group renders its view (divided list) or its inputs, switched by the one edit mode.
  // The first group carries the single Edit / Save+Cancel control in its header (top of the card).
  const section = (title: string, view: React.ReactNode, edit: React.ReactNode, action?: React.ReactNode) => (
    <section>
      <SectionHeader title={title} action={action} />
      {isEditing ? edit : view}
    </section>
  );

  return (
    <Card className="p-6 border-border">
      <div className="space-y-8">
        {/* Personal */}
        {section(
          'Personal',
          <InfoList>
            <InfoRow label="First Name" value={readVal(formData.firstName)} />
            <InfoRow label="Last Name" value={readVal(formData.lastName)} />
            <InfoRow label="Email Address" value={formData.email} labelExtra={emailBadge} />
            <InfoRow label="Phone Number" value={readVal(formData.phone)} />
          </InfoList>,
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {field('firstName', 'First Name', 'First name')}
              {field('lastName', 'Last Name', 'Last name')}
            </div>
            <div>
              <label className={`${fieldLabel} flex items-center gap-2`}>
                Email Address
                {emailBadge}
              </label>
              <p className="text-sm text-foreground">{formData.email}</p>
              <p className="mt-1 text-xs text-muted-foreground">Email cannot be changed</p>
            </div>
            {field('phone', 'Phone Number', '(555) 123-4567')}
          </div>,
          editControls,
        )}

        {/* Address */}
        {section(
          'Address',
          <InfoList>
            <InfoRow label="Address Line 1" value={readVal(formData.addressLine1)} />
            <InfoRow label="Address Line 2" value={readVal(formData.addressLine2)} />
            <InfoRow label="City" value={readVal(formData.city)} />
            <InfoRow label="State" value={readVal(formData.state)} />
            <InfoRow label="ZIP Code" value={readVal(formData.zipCode)} />
            <InfoRow label="Country" value={readVal(formData.country)} />
          </InfoList>,
          <div className="space-y-4">
            {field('addressLine1', 'Address Line 1', 'Street address')}
            {field('addressLine2', 'Address Line 2', 'Apt, suite, unit, etc.')}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
              {field('city', 'City', 'City')}
              {field('state', 'State', 'State')}
              {field('zipCode', 'ZIP Code', 'ZIP')}
              {field('country', 'Country', 'Country')}
            </div>
          </div>,
        )}

        {/* Business */}
        {section(
          'Business Information',
          <InfoList>
            <InfoRow label="Business Name" value={readVal(formData.businessName)} />
            <InfoRow label="Business Address" value={readVal(formData.businessAddress)} />
          </InfoList>,
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {field('businessName', 'Business Name', 'Business name (optional)')}
            {field('businessAddress', 'Business Address', 'Business address (optional)')}
          </div>,
        )}

        {/* Account Details — read-only */}
        <section>
          <SectionHeader title="Account Details" />
          <InfoList>
            <InfoRow label="Account Type" value={<span className="capitalize">{user?.role || '—'}</span>} />
            <InfoRow label="Member Since" value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : '—'} />
            <InfoRow label="Wallet Address" value={<span className="break-all">{user?.wallet_address || 'Not connected'}</span>} />
            <InfoRow label="Verification Status" value={<KycStatusBadge asText />} />
          </InfoList>
        </section>
      </div>
    </Card>
  );
}

export default ProfileSection;
